/**
 * F-170 (B4.2) — Cart page.
 *
 * Two routes share this page:
 *   `/cart`                    → list of all open carts grouped by specialist
 *   `/cart/<specialistId>`     → single-specialist cart with qty stepper
 *
 * On Оформити: open ProductOrderForm; on submit the form posts
 * `POST /carts/<id>/checkout/` and navigates the user into the freshly
 * created per-order chat (B0.12).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Button, EmptyState, ErrorState, Icon, Spinner } from '@/components/atoms';
import { toast } from '@/components/atoms/Toast';
import { useCartStore } from '@/store';
import { carts as cartsApi } from '@/api';
import type { Cart } from '@/api/carts';
import { resolveMediaUrl } from '@/utils/media';
import { logger } from '@/utils/logger';
import { ProductOrderForm } from '@/components/domain/ProductOrderForm';
import styles from './pages.module.css';

function CartRow({
  cart,
  onChanged,
  onCheckout,
}: {
  cart: Cart;
  onChanged: () => void;
  onCheckout: () => void;
}): JSX.Element {
  const [busyItem, setBusyItem] = useState<number | null>(null);

  const adjust = async (itemId: number, qty: number): Promise<void> => {
    if (qty < 1) return;
    setBusyItem(itemId);
    try {
      await cartsApi.updateItem(cart.id, itemId, qty);
      onChanged();
    } catch (err) {
      logger.warn('cart updateItem failed', err);
      toast.error('Не вдалось оновити кількість');
    } finally {
      setBusyItem(null);
    }
  };

  const remove = async (itemId: number): Promise<void> => {
    setBusyItem(itemId);
    try {
      await cartsApi.removeItem(cart.id, itemId);
      onChanged();
    } catch (err) {
      logger.warn('cart removeItem failed', err);
      toast.error('Не вдалось видалити позицію');
    } finally {
      setBusyItem(null);
    }
  };

  return (
    <section className={styles.cartCard}>
      <ul className={styles.cartItems}>
        {cart.items.map((it) => {
          const busy = busyItem === it.id;
          return (
            <li key={it.id} className={styles.cartItem}>
              {it.product_image_url ? (
                <img
                  src={resolveMediaUrl(it.product_image_url)}
                  alt=""
                  className={styles.cartItemImg}
                  loading="lazy"
                />
              ) : (
                <div className={styles.cartItemImgPlaceholder} aria-hidden>
                  <Icon name="package" size={20} />
                </div>
              )}
              <div className={styles.cartItemBody}>
                <div className={styles.cartItemTitle}>{it.product_title}</div>
                <div className={styles.cartItemPrice}>
                  {Number(it.price_eur_at_add).toFixed(2)} €
                </div>
                <div className={styles.cartStepper}>
                  <button
                    type="button"
                    onClick={() => void adjust(it.id, it.qty - 1)}
                    disabled={busy || it.qty <= 1}
                    aria-label="Менше"
                  >
                    <Icon name="minus" size={14} />
                  </button>
                  <span aria-label={`Кількість ${it.qty}`}>{it.qty}</span>
                  <button
                    type="button"
                    onClick={() => void adjust(it.id, it.qty + 1)}
                    disabled={busy}
                    aria-label="Більше"
                  >
                    <Icon name="plus" size={14} />
                  </button>
                </div>
              </div>
              <div className={styles.cartItemRight}>
                <div className={styles.cartItemTotal}>
                  {Number(it.line_total_eur).toFixed(2)} €
                </div>
                <button
                  type="button"
                  className={styles.cartItemRemove}
                  onClick={() => void remove(it.id)}
                  disabled={busy}
                  aria-label="Видалити"
                >
                  <Icon name="trash" size={16} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      <div className={styles.cartFooter}>
        <div className={styles.cartTotal}>
          <span>Разом</span>
          <strong>{Number(cart.total_eur).toFixed(2)} €</strong>
        </div>
        <Button onClick={onCheckout} disabled={cart.items.length === 0}>
          Оформити
        </Button>
      </div>
    </section>
  );
}

export function CartPage(): JSX.Element {
  const navigate = useNavigate();
  const { specialistId = '' } = useParams<{ specialistId?: string }>();
  const isAllCarts = !specialistId;
  const [loading, setLoading] = useState(true);
  const [carts, setCarts] = useState<Cart[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [checkoutCart, setCheckoutCart] = useState<Cart | null>(null);
  // F-180 C13 — keep the NavBar cart pill in sync with the page.
  const setCartCount = useCartStore((s) => s.setCount);

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      if (isAllCarts) {
        const { results } = await cartsApi.fetchOpenCarts();
        setCarts(results);
        setCartCount(results.length);
      } else {
        const { cart } = await cartsApi.fetchActiveCart(specialistId);
        setCarts(cart ? [cart] : []);
      }
    } catch (err) {
      logger.warn('Cart fetch failed', err);
      // F-180 C4: distinguish load failure from a genuinely empty cart
      // so the empty-cart CTA does not mask network / auth errors.
      setError(err instanceof Error ? err : new Error(String(err)));
      setCarts([]);
    } finally {
      setLoading(false);
    }
  }, [isAllCarts, specialistId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const title = isAllCarts ? 'Кошик' : 'Кошик спеціаліста';
  const single = useMemo(() => (isAllCarts ? null : carts[0] ?? null), [isAllCarts, carts]);

  return (
    <Layout title={title}>
      {loading ? (
        <Spinner center />
      ) : error ? (
        <ErrorState onRetry={() => void reload()} />
      ) : isAllCarts ? (
        carts.length === 0 ? (
          <EmptyState
            icon="shopping-cart"
            title="Поки порожньо"
            message="Знайди майстра та додай товар — кошик зʼявиться тут."
            action={{ label: 'До пошуку', to: '/search' }}
          />
        ) : (
          <div className={styles.cartList}>
            {carts.map((cart) => (
              <div key={cart.id} className={styles.cartGroup}>
                <div className={styles.cartGroupHead}>
                  <span>{cart.item_count} позицій</span>
                  <Link to={`/cart/${cart.specialist_id}`} className={styles.cartGroupOpen}>
                    Відкрити
                  </Link>
                </div>
                <CartRow
                  cart={cart}
                  onChanged={() => void reload()}
                  onCheckout={() => setCheckoutCart(cart)}
                />
              </div>
            ))}
          </div>
        )
      ) : single == null || single.items.length === 0 ? (
        <EmptyState
          icon="shopping-cart"
          title="Кошик порожній"
          message="Повернись у профіль майстра та додай товари."
          action={{ label: 'До профілю', to: `/u/${specialistId}` }}
        />
      ) : (
        <CartRow
          cart={single}
          onChanged={() => void reload()}
          onCheckout={() => setCheckoutCart(single)}
        />
      )}

      {checkoutCart ? (
        <ProductOrderForm
          open
          cart={checkoutCart}
          onClose={() => setCheckoutCart(null)}
          onCheckedOut={(chatId) => {
            setCheckoutCart(null);
            navigate(`/chats/${chatId}`);
          }}
        />
      ) : null}
    </Layout>
  );
}
